/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import { useEffect } from 'react';
import { useQuery } from 'react-apollo';
import { differenceInDays } from 'date-fns';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { fg } from '@confluence/feature-gating';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition';
import { useSessionData } from '@confluence/session-data';

import type { usePublicLinkDisableTrialDetailsQuery as usePublicLinkDisableTrialDetailsQuery$data } from './__types__/usePublicLinkDisableTrialDetailsQuery';

type PublicLinkDisable = {
	shouldRenderDisablePublicLinkFeature: boolean;
};

type PublicLinkDisableParams = {
	source?: string;
};

const getTrialDaysLeftFromQuery = (
	queryData: usePublicLinkDisableTrialDetailsQuery$data | undefined,
) => {
	const trialEndDate = queryData?.license?.trialEndDate;
	return getTrialDaysLeft(trialEndDate);
};

const getTrialDaysLeft = (trialEndDate: string | null | undefined) => {
	if (!trialEndDate) {
		return 0;
	}

	// +1 day to round up for fractional days
	return differenceInDays(new Date(trialEndDate), new Date()) + 1;
};

export const DISABLE_PUBLIC_LINKS_ON_TRIAL_GATE = 'confluence_disable_public_links_on_trial';

export const usePublicLinkDisable = ({ source }: PublicLinkDisableParams): PublicLinkDisable => {
	const { isLoggedIn, userId, edition } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const isFreeEdition = edition === ConfluenceEdition.FREE;

	const skipTrialDetailsQuery = isFreeEdition || !isLoggedIn || !userId;

	const {
		data: trialDetailsData,
		error: trialDetailsError,
		loading: trialDetailsLoading,
	} = useQuery<usePublicLinkDisableTrialDetailsQuery$data>(
		gql`
			query usePublicLinkDisableTrialDetailsQuery {
				license {
					trialEndDate
				}
			}
		`,
		{
			skip: skipTrialDetailsQuery,
		},
	);

	const daysLeft =
		!trialDetailsError && !trialDetailsLoading ? getTrialDaysLeftFromQuery(trialDetailsData) : 0;

	const isTrialEdition = daysLeft > 0;
	const shouldRenderDisablePublicLinkFeature =
		(isTrialEdition || isFreeEdition) && fg('confluence_disable_public_links_on_trial');

	useEffect(() => {
		if (source && (isFreeEdition || isTrialEdition)) {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					actionSubject: 'feature',
					action: 'exposed',
					source,
					attributes: {
						flagKey: DISABLE_PUBLIC_LINKS_ON_TRIAL_GATE,
						flagValue: fg('confluence_disable_public_links_on_trial'),
						touchpointId: source,
						edition,
					},
				},
			}).fire();
		}
	}, [
		createAnalyticsEvent,
		edition,
		isFreeEdition,
		isTrialEdition,
		shouldRenderDisablePublicLinkFeature,
		source,
	]);

	return { shouldRenderDisablePublicLinkFeature };
};
